import { IonCol, IonItem, IonList, IonRow, useIonViewDidEnter, useIonViewDidLeave } from '@ionic/react'
import React, { useEffect, useState } from 'react'
import MultiSelect from '../../../../components/multiSelect/MultiSelect.component'
import { useTranslation } from 'react-i18next'
import { IOption, IPNNVOption } from '../../../../common/common.types'
import Comment from '../../../../components/comment/Comment.component'
import SingleSelect from '../../../../components/singleSelect/SingleSelect.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { getInitialAssessmentAirwayOptionsSelector } from '../../../options/selectors/initalAssessmentAirwayOptions.selectors'
import { useDispatch, useSelector } from 'react-redux'
import {
  saveInitialAssessmentAirway,
  setDepth,
  setExam,
  setLengthBasedTapeMeasure,
  setLengthBasedTapeMeasurePNNV,
  setOutcome,
  setPerformedBy,
  setSecuredVia,
  setStatus,
  setTubeSize,
  setTubeSizeUnit,
  setTubeSizeUnitClient,
  setVerificationMethod,
} from '../store/airway.actions'
import { getInitialAssessmentAirway } from '../selectors/getInitialAssessmentAirway.selectors'
import { getPatientDemographicsSelector } from '../../../patientDemographics/selectors'
import { getAge } from '../../../../utilities/showAssessmentAirwayFields.util'
import NumberPad from '../../../../components/numberPad/NumberPad.component'
import './Airway.content.css'
import { hyphenDotKeys } from '../../../../components/numberPad/NumberPad.types'
import Validation from '../../../../components/validation/Validation.component'
import { AirwayConstants } from '../store/airway.constants'
import useDeviceMetrics from '../../../../utilities/useDeviceMetrics'

const AirwayContent: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isTablet } = useDeviceMetrics()
  const initialAssessmentAirwayOptions = useSelector(getInitialAssessmentAirwayOptionsSelector)
  const {
    airStatusOptions,
    airTypeOptions,
    airEttSizeUnitOptions,
    airByOptions,
    airOutcomeOptions,
    prAirwayVerificationAssessOptions,
    prAirwayAssessOptions,
    airBroselowOptions,
  } = initialAssessmentAirwayOptions

  const initialAssessmentAirway = useSelector(getInitialAssessmentAirway)

  const { prAirwayVerificationAssess, prAirwayAssess, prNabc } = initialAssessmentAirway
  const {
    airStatus,
    airType,
    airComments,
    airEttSize,
    airEttLips,
    airBy,
    airOutcome,
    broselow,
    broselowPNNV,
    __airEttSizeUnit,
  } = prNabc

  const patientDemographics = useSelector(getPatientDemographicsSelector)
  const { age, ageUnit } = patientDemographics
  const showLengthBasedTapeMeasured =
    Number(age) >= 1 && ageUnit?.id && getAge(Number(age), ageUnit?.id?.toString()) <= 13 ? true : false
  const isSecuredTubeDepthFieldsShow: boolean = airStatus?.id === 21 || airStatus?.id === 2

  const [airwayValidationFlag, setAirwayValidationFlag] = useState(true)
  const setLengthBasedTapeMeasureOnChange = (option: IOption) => {
    dispatch(setLengthBasedTapeMeasure(option))
    dispatch(saveInitialAssessmentAirway())
  }

  const setLengthBasedTapeMeasurePNNVOnChange = (option: IPNNVOption | null) => {
    dispatch(setLengthBasedTapeMeasurePNNV(option))
    dispatch(saveInitialAssessmentAirway())
  }

  const setStatusOnChange = (option: IOption) => {
    dispatch(setStatus(option))
    dispatch(saveInitialAssessmentAirway())
  }

  const setSecuredViaOnChange = (option: IOption) => {
    dispatch(setSecuredVia(option))
    dispatch(saveInitialAssessmentAirway())
  }

  const setTubeSizeOnChange = (numbers: string[]) => {
    let newTubeSize = ''
    if (numbers.length) {
      newTubeSize = numbers.join('')
    }
    dispatch(setTubeSize(newTubeSize))
    dispatch(saveInitialAssessmentAirway())
  }

  const setTubeSizeUnitOnChange = (option: IOption) => {
    dispatch(setTubeSizeUnit(option.name))
    dispatch(setTubeSizeUnitClient(option))
    dispatch(saveInitialAssessmentAirway())
  }

  const setDepthOnChange = (numbers: string[]) => {
    let newDepth = ''
    if (numbers.length) {
      newDepth = numbers.join('')
    }
    dispatch(setDepth(newDepth))
    dispatch(saveInitialAssessmentAirway())
  }

  const setPerformedByOnChange = (option: IOption) => {
    dispatch(setPerformedBy(option))
    dispatch(saveInitialAssessmentAirway())
  }
  const setOutcomeOnChange = (option: IOption) => {
    dispatch(setOutcome(option))
    dispatch(saveInitialAssessmentAirway())
  }

  useEffect(() => {
    if (airwayValidationFlag) {
      airEttSize && dispatch(setTubeSize(airEttSize))
      airEttLips && dispatch(setDepth(airEttLips))
    }
  }, [airwayValidationFlag])

  useIonViewDidEnter(() => {
    setAirwayValidationFlag(true)
  })

  useIonViewDidLeave(() => {
    setAirwayValidationFlag(false)
  })

  return (
    <>
      <IonList id='airwayContent' className={isTablet ? 'ion-margin-horizontal' : ''}>
        <IonRow>
          <IonCol className='picklist ion-no-margin'>
            <MultiSelect
              showChips={true}
              dataTestID='VerificationMethod'
              listHeader={t('initialAssessmentAirway.verificationMethod')}
              options={prAirwayVerificationAssessOptions}
              savedValues={prAirwayVerificationAssess}
              action={setVerificationMethod}
              label={t('initialAssessmentAirway.verificationMethod')}
              callback={saveInitialAssessmentAirway}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        {showLengthBasedTapeMeasured && (
          <>
            <IonRow>
              <IonCol className='picklist font-roboto ion-no-padding'>
                <SingleSelect
                  dataTestID='LengthBasedTapeMeasure'
                  listHeader={t('initialAssessmentAirway.LengthBasedTapeMeasure')}
                  options={airBroselowOptions}
                  selectedOption={broselow}
                  action={setLengthBasedTapeMeasureOnChange}
                  label={t('initialAssessmentAirway.LengthBasedTapeMeasure')}
                  selectedPNNVOption={broselowPNNV}
                  pnnvAction={setLengthBasedTapeMeasurePNNVOnChange}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
          </>
        )}
        <IonRow>
          <IonCol className='picklist'>
            <SingleSelect
              dataTestID='Status'
              listHeader={t('initialAssessmentAirway.Status')}
              options={airStatusOptions}
              selectedOption={airStatus}
              action={setStatusOnChange}
              label={t('initialAssessmentAirway.Status')}
            />
          </IonCol>
        </IonRow>
        <hr className='field-divider' />
        {isSecuredTubeDepthFieldsShow && (
          <>
            <IonRow>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='SecuredVia'
                  listHeader={t('initialAssessmentAirway.SecuredVia')}
                  options={airTypeOptions}
                  selectedOption={airType}
                  action={setSecuredViaOnChange}
                  label={t('initialAssessmentAirway.SecuredVia')}
                />
              </IonCol>
            </IonRow>
            <hr className='field-divider' />
            <IonRow>
              <IonCol className='picklist'>
                <NumberPad
                  className='airway-numberpad'
                  elementId='TubeSize'
                  dataTestId='TubeSize'
                  values={airEttSize ? airEttSize.toString().split('') : []}
                  label={t('initialAssessmentAirway.TubeSize')}
                  onNumPadClick={setTubeSizeOnChange}
                  validCharacters={hyphenDotKeys}
                />
              </IonCol>
              <IonCol className='picklist'>
                <SingleSelect
                  dataTestID='TubeSizeUnit'
                  listHeader={t('initialAssessmentAirway.Unit')}
                  options={airEttSizeUnitOptions}
                  selectedOption={__airEttSizeUnit}
                  action={setTubeSizeUnitOnChange}
                  label={t('initialAssessmentAirway.Unit')}
                />
              </IonCol>
            </IonRow>
            <Validation id={AirwayConstants.SET_TUBE_SIZE} />
            <hr className='field-divider' />
            <IonRow>
              <IonCol className='picklist'>
                <NumberPad
                  className='airway-numberpad'
                  elementId='Depth'
                  dataTestId='Depth'
                  values={airEttLips ? airEttLips.toString().split('') : []}
                  label={t('initialAssessmentAirway.Depth')}
                  onNumPadClick={setDepthOnChange}
                  validCharacters={hyphenDotKeys}
                />
              </IonCol>
              <IonCol className='font-roboto ion-no-padding'>
                <IonItem className='depth-unit' lines='none'>
                  <span>cm</span>
                </IonItem>
              </IonCol>
            </IonRow>
            <Validation id={AirwayConstants.SET_DEPTH} />
            <hr />
          </>
        )}
        <IonRow>
          <IonCol className='ion-no-padding'>
            <Comment
              isDataPresent={airComments.length > 0 ? true : false}
              path={TabConstants.INITIAL_ASSESSMENT_COMMENTS}
            />
          </IonCol>
        </IonRow>

        <hr className='field-divider' />
        <IonRow>
          <IonCol className='picklist' size='6'>
            <SingleSelect
              dataTestID='PerformedBy'
              listHeader={t('initialAssessmentAirway.PerformedBy')}
              options={airByOptions}
              selectedOption={airBy}
              action={setPerformedByOnChange}
              label={t('initialAssessmentAirway.PerformedBy')}
            />
            <hr className='field-divider' />
          </IonCol>
          <IonCol className='picklist' size='6'>
            <SingleSelect
              dataTestID='Outcome'
              listHeader={t('initialAssessmentAirway.Outcome')}
              options={airOutcomeOptions}
              selectedOption={airOutcome}
              action={setOutcomeOnChange}
              label={t('initialAssessmentAirway.Outcome')}
            />
            <hr className='field-divider' />
          </IonCol>
        </IonRow>
        {prAirwayAssessOptions.length > 0 && (
          <>
            <IonRow>
              <IonCol className='picklist ion-no-margin'>
                <MultiSelect
                  showChips={true}
                  dataTestID='AirwayExam'
                  listHeader={t('initialAssessmentAirway.AirwayExam')}
                  options={prAirwayAssessOptions}
                  savedValues={prAirwayAssess}
                  action={setExam}
                  label={t('initialAssessmentAirway.AirwayExam')}
                  callback={saveInitialAssessmentAirway}
                />
              </IonCol>
            </IonRow>
          </>
        )}
      </IonList>
    </>
  )
}

export default AirwayContent
